import { useEffect, useMemo } from 'react'

import { DefaultVariableResult, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'

import { useGlobalVariableValues } from '@/core/app-builder/global-variables'
import { parseVariableSelect } from '@/core/app-builder/parser/variable-select'
import { ICell } from '@/core/models/columns'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'

import { ColBrief, getColBrief } from '../utils'
import { CellComponent } from './CellComponent'

/**
 * CompVariableSelectGlobalVariable
 */
const CompVariableSelectGlobalVariable = ({ item }: { item: ParsedSelectValue }) => {
  const { getVariableValue } = useGlobalVariableValues()
  const value = getVariableValue(item.value)
  return <>{value}</>
}

function getTargetTableId(item: DefaultVariableResult, activeLayerTableId?: string, tbId?: string) {
  let tableId: string | undefined = undefined

  switch (item.flag) {
    case VariableSelectFeatureFlag.ColumnData:
    case VariableSelectFeatureFlag.SingleCell:
      tableId = item.tbl
      break
    case VariableSelectFeatureFlag.CurrentRow:
      tableId = activeLayerTableId
      break
    case VariableSelectFeatureFlag.Column:
    case VariableSelectFeatureFlag.CurrentListItem:
      tableId = tbId
      break
  }
  return tableId
}

function getTargetRowId(item: DefaultVariableResult, activeLayerDetailRowId?: string, rowId?: string) {
  let targetRowId: string | undefined = undefined

  switch (item.flag) {
    case VariableSelectFeatureFlag.CurrentRow:
    case VariableSelectFeatureFlag.Column:
      targetRowId = activeLayerDetailRowId
      break
    default:
      targetRowId = rowId
      break
  }
  return targetRowId
}

interface ParsedSelectValue {
  flag: VariableSelectFeatureFlag
  value: any
  colBrief: ColBrief | undefined
  cell: ICell | undefined
}

interface CompVariableSelectSingleProps {
  item: ParsedSelectValue
}

function CompVariableSelectSingle({ item }: CompVariableSelectSingleProps) {
  // render column cell component
  if (!!item.colBrief && !!item.cell) {
    return <CellComponent cell={item.cell} type={item.colBrief._Type} />
  }

  // render other types
  switch (item.flag) {
    case VariableSelectFeatureFlag.Icon: {
      return <MaterialIcon className='shrink-0' icon={item.value} />
    }
    case VariableSelectFeatureFlag.Image: {
      return <img src={item.value} />
    }
    case VariableSelectFeatureFlag.Space: {
      return <>&nbsp;</>
    }
    case VariableSelectFeatureFlag.GlobalVariable: {
      return <CompVariableSelectGlobalVariable item={item} />
    }
    default:
      return <>{item.value}</>
  }
}

const FLAGS_WITH_COLUMN = [
  VariableSelectFeatureFlag.Column,
  VariableSelectFeatureFlag.ColumnData,
  VariableSelectFeatureFlag.CurrentListItem,
  VariableSelectFeatureFlag.CurrentRow,
  VariableSelectFeatureFlag.SingleCell,
]

interface CompVariableSelectProps {
  items?: DefaultVariableResult[]
  tableId: string
  rowId: string
}

/**
 * CompVariableSelect
 */
export function CompVariableSelect({ items = [], tableId, rowId }: CompVariableSelectProps) {
  const { dbClient, language, userSpecific, dbWorker, loadedTables, tablesMap } = useDbClientContext()
  const { activeLayerClient, activeLayerDetailRowId } = useActiveLayerContext()
  const { userEmail } = useAppAuthContext()

  const fixedItems = useMemo(() => {
    if (!Array.isArray(items) && items !== null && items !== undefined) {
      return [items]
    }
    return items || []
  }, [items])

  const parsedItems = useMemo(() => {
    if (!dbClient || !activeLayerClient) return []

    const table = tablesMap.get(tableId)
    if (!table) return []

    try {
      const parsedItems = fixedItems.map((item) => {
        const targetRowId = getTargetRowId(item, activeLayerDetailRowId, rowId)
        const targetTableId = getTargetTableId(item, activeLayerClient.settings._TableID, tableId)
        const colBrief =
          FLAGS_WITH_COLUMN.includes(item.flag) && 'col' in item ? getColBrief(table, item.col) : undefined

        const rowData = table.GetRowDataByRowId(targetRowId ?? '', language, userSpecific)

        const val = parseVariableSelect(item, dbClient, userEmail, language, userSpecific, targetTableId, targetRowId)

        return {
          flag: item.flag,
          value: val?.[0],
          colBrief,
          cell: colBrief && rowData ? rowData[colBrief._id] : undefined,
        } as ParsedSelectValue
      })
      return parsedItems
    } catch (err) {
      // todo
      return []
    }
  }, [
    activeLayerClient,
    activeLayerDetailRowId,
    dbClient,
    tablesMap,
    fixedItems,
    language,
    rowId,
    tableId,
    userEmail,
    userSpecific,
  ])

  useEffect(() => {
    if (!dbClient || !dbWorker || !tableId) return

    if (!loadedTables.has(tableId)) {
      dbWorker.sendAction({
        type: 'get_table_data',
        payload: {
          table_id: tableId,
          language,
        },
        meta: { from: 'CompVariableSelect' },
      })
    }
  }, [dbClient, dbWorker, language, loadedTables, tableId])

  return (
    <>
      {parsedItems.map((item, i) => (
        <CompVariableSelectSingle key={i} item={item} />
      ))}
    </>
  )
}
